import {StyledDiv, StyledHR, StyledInput, StyledLabel, StyledSpan} from './StyledTypeDefinitions'
import styled, {css, FlattenSimpleInterpolation} from 'styled-components'
import {BreakPoint, Colors, FontSize, FontWeights, LineHeight} from './Constants'
import {RowFlexContainer} from './FlexComponents'
import {generateMedia} from 'styled-media-query'
import {Nullable} from '../app/TypeHelpers'
import { currentMedia } from './ResponsiveConvenience'

// ブレイクポイント
const breakPoint: any = generateMedia({
    sp: `${BreakPoint.Sp}`,
})

const displayItemHorizontalPadding: string = '15px'

export const DisplayItem: StyledDiv = styled.div`
  background-color: ${Colors.Surface};
  padding: 2rem ${displayItemHorizontalPadding};
`

export const DisplayBlockDiv: StyledDiv = styled.div`
  margin-bottom: ${displayItemHorizontalPadding};
`

export const DisplayItemTitle = styled(RowFlexContainer)`
  font-size: ${FontSize.Accent};
  color: ${Colors.AccentDark};
  font-weight: ${FontWeights.Bold};
  line-height: 2rem;
  justify-content: flex-start;
  position: relative;
  box-sizing: border-box;

  &::before {
    content: "";
    background: linear-gradient(96deg, #0070B0 50%, #00497F 50%);
    height: 4rem;
    width: 0.5rem;
    display: inline-block;
    margin-right: 1rem;
  }
`

export const DisplayItemTitleSmall = styled(DisplayItemTitle)`
  font-size: ${FontSize.Body};

  &::before {
    height: 2.6rem;
  }
`

export const DisplayItemLabelDiv: StyledDiv = styled.div`
  color: ${Colors.AccentDark};
  margin-bottom: 2px;
  line-height: 1.5;
`
export const DisplayItemLabelDivNoMargin = styled(DisplayItemLabelDiv)`
  margin: 0;
`

export const DisplayItemDiv: StyledDiv = styled.div`
  margin-left: 15px;
  line-height: 1.5;
  word-break : break-all;
`

// 項目（任意）
export const DisplayItemInputLabel: StyledLabel = styled.label`
  color: ${Colors.AccentDark};
  margin-bottom: 2px;

  &:last-child {
    margin-bottom: 0;
  }
`

// 項目（必須）
export const DisplayItemInputLabelRequired = styled(DisplayItemInputLabel)`
  position: relative;
  margin-bottom: 15px;
`

// 項目名（任意）
export const DisplayItemInputTitle: StyledDiv = styled.div`
  line-height: ${LineHeight.Basic};
  margin-bottom: 10px;
`
export const DisplayItemInputTitleNoMargin = styled(DisplayItemInputTitle)`
  color: ${Colors.AccentDark};
  margin-bottom: 0;
`

const RequiredLabelStyle: FlattenSimpleInterpolation = css`
    &:after {
      content: "必須";
      margin: 0 0 0 10px;
      padding: 3px;
      color: #fff;
      font-size: 75%;
      background-color: ${Colors.Alert};
      white-space: nowrap;
  }
`
// 項目名（必須）
export const DisplayItemInputTitleRequired = styled(DisplayItemInputTitle)`
  ${RequiredLabelStyle};
`
export const DisplayItemInputTitleRequiredNoMargin = styled(DisplayItemInputTitleNoMargin)`
  ${RequiredLabelStyle};
`

const clearBrowserElementStyles: FlattenSimpleInterpolation = css`
  appearance:none;
  -webkit-appearance: none;
  -moz-appearance: none;

  &:focus {
    outline-style: none;
  }
`

const displayItemInputTextStyle: FlattenSimpleInterpolation = css`
  color: ${Colors.Body};
  font-weight: ${FontWeights.Normal};
  line-height: 1.5;
`

// 入力フォームProps
export interface InputErrorProps {
    hasError?: Nullable<boolean>;
}

const disabledFormStyle: FlattenSimpleInterpolation = css`
    pointer-events: none;
    border: 1px solid ${Colors.AccentLight};
    background-color: rgba(217, 217, 217, 0.5);
    opacity: 0.5;
`

// 入力フォーム（Common）
export const displayItemInputCommonStyle: FlattenSimpleInterpolation = css`
  position: relative;
  box-sizing: border-box;
  height: 50px;
  width: 100%;

  text-indent: .01px;
  text-overflow: "";
  ${clearBrowserElementStyles};

  border: ${Colors.AccentLight} 1px solid;
  border-radius: 4px;
  padding: 0 15px 0 15px;

  font-size: ${FontSize.Body};
  ${displayItemInputTextStyle}

  &:focus {
    border-color: ${Colors.AccentDark};
  }
  :disabled {
    ${disabledFormStyle}
  }
`

// フォーム
export const DisplayItemInput = styled.input<InputErrorProps>`
  ${displayItemInputCommonStyle};

  border:  ${props => props.hasError === true ? `${Colors.Alert} 1px solid` : `${Colors.AccentLight} 1px solid`};
  ${props => props.hasError === true ? `background-color: rgb(253, 245, 246)` : ``};

  &:focus {
    border-color:  ${props => props.hasError === true ? `${Colors.Alert}` : `${Colors.AccentDark}`};
  }
`

// フォーム（プレースホルダー）
export const DisplayItemTextInput = styled(DisplayItemInput)`
  ::placeholder {
    color: ${Colors.Placeholder}
  }
`

// セレクトボックス（Common）
export const displayItemSelectCommonStyle: FlattenSimpleInterpolation = css`
  position: relative;
  box-sizing: border-box;
  height: 50px;
  width: 100%;

  text-indent: .01px;
  text-overflow: "";
  ${clearBrowserElementStyles};

  border: ${Colors.AccentLight} 1px solid;
  border-radius: 4px;
  background-color: ${Colors.Surface};
  padding: 0 15px 0 15px;
  font-size: ${FontSize.Body};

  ${displayItemInputTextStyle}

  &::-ms-expand {
    display: none;
  }

  &:focus {
    border-color: ${Colors.AccentDark};
  }

  :disabled {
    ${disabledFormStyle}
  }
`

// セレクトボックス
export const DisplayItemSelect = styled.select<InputErrorProps>`
  ${displayItemSelectCommonStyle};

   border:  ${props => props.hasError === true ? `${Colors.Alert} 1px solid` : `${Colors.AccentLight} 1px solid`};
  ${props => props.hasError === true ? `background-color: rgba(215, 55, 74, 0.05)` : ``};
`

export const DisplayItemSelectLikeDiv = styled.div<InputErrorProps & { disabled?: boolean }>`
  ${displayItemSelectCommonStyle};
  border:  ${props => props.hasError === true ? `${Colors.Alert} 1px solid` : `${Colors.AccentLight} 1px solid`};
  display: flex;
  align-items: center;
  ${props => props.disabled === true ? `${disabledFormStyle}` : ``};
  ${props => props.hasError === true ? `background-color: rgba(215, 55, 74, 0.05)` : ``};
`

// セレクトボックス(矢印アイコン)
export const DisplayItemSelectIcon: StyledDiv = styled.div`
   position: relative;

   &::after {
     display: block;
     position: absolute;
     width: 6px;
     height: 6px;
     margin-top: -5px;
     border: 0;
     border-bottom: solid 1px ${Colors.AccentDark};
     border-right: solid 1px ${Colors.AccentDark};
     transform: rotate(45deg);
     top: 50%;
     right: 2%;
     pointer-events: none;
     content: '';
     }
`

const iconDownChevron: string = `/resources/images/icons/icon_down_chevron.svg`
const selectDownChevronAfterElement: FlattenSimpleInterpolation = css`
  &::after {
    pointer-events: none;
    content: "";
    display: inline-block;
    width: 1.8rem;
    height: .8rem;
    margin-right: 0;
    margin-left: auto;
    background-image: url(${iconDownChevron});
  }
`

export const DisplayItemPickerInput: StyledInput = styled(DisplayItemInput)`
  ${selectDownChevronAfterElement}

  ::-webkit-clear-button {
    -webkit-appearance: none;
  }

  ::-webkit-calendar-picker-indicator {
    position: absolute;
    top: 0;
    right: 1.5rem;
    width: 2rem;
    height: 100%;
    background: transparent;
    color: transparent;
  }
`

export const DisplayItemSelectWrapper = styled(RowFlexContainer)`
  ${selectDownChevronAfterElement};
  ${displayItemInputCommonStyle};

  padding: 0;
  border: none;

  &::after {
    position: absolute;
    right: 1.5rem;
  }
`

export const DisplayItemData: StyledDiv = styled.div`
  font-weight: ${FontWeights.Bold};
  line-height: 1;

  &:not(:last-child) {
    margin-bottom: 0.8rem;
  }
`

export const DisplayItemFacilityOptionTitle = styled.div<{ required?: boolean }>`
  font-weight: ${FontWeights.Normal};
  color: ${Colors.AccentDark};
  ${props => props.required ? RequiredLabelStyle : ''};
`

export const DisplayItemFacilityOptionSubtitle: StyledDiv = styled.div`
  font-size: ${FontSize.Middle};
  color: ${Colors.AccentDark};
  line-height: 1;
`

export const DisplayItemDivider: StyledHR = styled.hr`
  height: 1px;
  border: none;
  width: calc(100% + 3rem);
  padding: 0;
  margin: 0 0 0 -1.5rem;
  border-bottom: 1px solid ${Colors.Background};
`
export const DisplayItemDividerForPageTop: StyledHR = styled.hr`
  height: 1px;
  border: none;
  width: calc(100% + 3rem);
  padding: 0;
  margin: 0 0 0 -1.5rem;
  border-bottom: 1px solid ${Colors.AccentLight};
`

// 入力フォーム（チェックボックス）
export const DisplayCheckItemSelectionLabel: StyledLabel = styled.label`
    display: table;
    word-wrap: break-word;
    word-break: break-all;
`

// 項目名（ 入力フォーム（チェックボックス））
export const DisplayCheckItemSelectionName: StyledDiv = styled.div`
    display: table-cell;
    padding-right: 0.5px;
`

// 入力フォーム（ラジオ）
export const DisplayRadioItemLabel: StyledLabel = styled.label`
    word-wrap: break-word;
    word-break: break-all;

  ${breakPoint.lessThan('sp')`
      display: block;
  `}
`

// 項目名（入力フォーム（ラジオ））
export const DisplayRadioItemName: StyledDiv = styled.div`
    display: table-cell;
    padding-right: 0.5px;
`


// テーブル
export const DisplayItemTable: StyledDiv = styled.div`
    display: table;
    padding: 15px;
    margin: 5px 0 5px 0;
    width: 100%;
    box-sizing: border-box;
    background-color: ${Colors.Surface};
    word-break : break-all;

  ${breakPoint.lessThan('sp')`
      display: block;
      width: 100%;
  `}
`

// 項目名（テーブル）
export const DisplayItemTableLabel: StyledDiv = styled.div`
    display: table-cell;
    margin-bottom: 2px;
    width: 300px;
    box-sizing: border-box;
    text-align: left;
    vertical-align: middle;
    color: ${Colors.AccentDark};

  ${breakPoint.lessThan('sp')`
      display: block;
      width: 100%;
  `}
`

// 入力内容（テーブル）
export const DisplayItemTableInput: StyledDiv = styled.div`
    padding: 0 0 0 15px;
    width: 100%;
    box-sizing: border-box;
    text-align: left;
    vertical-align: middle;

  ${breakPoint.lessThan('sp')`
      display: block;
      width: 100%;
  `}
`

// フォームエラーメッセージ
export const FieldErrorLabel: StyledSpan = styled.span`
  display: block;
  margin: 5px 0 0 0;
  color: ${Colors.Alert};
  white-space: pre-wrap;
  word-wrap: break-word;
`

export const FieldErrorSmallLabel = styled(FieldErrorLabel)`
  margin: 0;
  font-size: ${FontSize.Small};
  margin-top: 3px;
  margin-right: 14px;
  margin-bottom: 0;
  margin-left: 14px;
`

// タイトルエラーメッセージ
export const TitleErrorLabel: StyledDiv = styled.div`
  margin: 5px 0 0 0;
  color: ${Colors.Alert};
  white-space: pre-wrap;
  word-wrap: break-word;
`

export const BreakPcOnly = styled('br')`
  ${currentMedia.lessThan('sp')`
    display: none;
  `};
`
